/**
 * useTokenForm Hook
 * Custom hook for managing public token-based forms
 * Supports both single-step and multi-step forms
 */

import { useState, useEffect, useCallback } from 'react';
import publicTokenService from '../services/publicTokenService';

/**
 * Hook for managing token-based forms (public - no auth required)
 * @param {string} token - Token ID from URL
 * @returns {Object} Token form state and management functions
 */
export const useTokenForm = (token) => {
    const [tokenData, setTokenData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [formData, setFormData] = useState({});

    /**
     * Load token data and form schema
     */
    const loadToken = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);
            
            // Check if in test mode
            const testMode = sessionStorage.getItem('onboarding_test_mode') === 'true';
            
            if (testMode) {
                // Mock token data for test mode
                const mockTokenData = {
                    valid: true,
                    expiresAt: new Date(Date.now() + 72 * 60 * 60 * 1000).toISOString(),
                    purpose: 'client_onboarding',
                    formSchema: {
                        schema: {
                            title: 'Client Onboarding Form',
                            description: 'Please provide your information to get started with WETLAB.',
                            sections: [
                                {
                                    title: 'Company Information',
                                    fields: [
                                        {
                                            name: 'companyName',
                                            label: 'Company Name',
                                            type: 'text',
                                            required: true,
                                            placeholder: 'Enter your company name'
                                        },
                                        {
                                            name: 'industry',
                                            label: 'Industry',
                                            type: 'select',
                                            required: true,
                                            options: [
                                                { value: 'biotech', label: 'Biotechnology' },
                                                { value: 'pharma', label: 'Pharmaceutical' },
                                                { value: 'research', label: 'Research' },
                                                { value: 'other', label: 'Other' }
                                            ]
                                        }
                                    ]
                                },
                                {
                                    title: 'Contact Information',
                                    fields: [
                                        {
                                            name: 'contactName',
                                            label: 'Contact Name',
                                            type: 'text',
                                            required: true
                                        },
                                        {
                                            name: 'email',
                                            label: 'Email Address',
                                            type: 'email',
                                            required: true
                                        },
                                        {
                                            name: 'phone',
                                            label: 'Phone Number',
                                            type: 'tel',
                                            required: false
                                        }
                                    ]
                                }
                            ]
                        },
                        theme: 'default',
                        styling: {},
                        behavior: {}
                    }
                };
                setTokenData(mockTokenData);
                setLoading(false);
                return;
            }
            
            const data = await publicTokenService.validateToken(token);
            setTokenData(data);

            if (!data.valid) {
                setError(data.message || 'Invalid token');
            } else if (data.payload) {
                // Prefill form with payload data
                setFormData(data.payload);
            }
        } catch (err) {
            console.error('Failed to load token:', err);
            setError(err instanceof Error ? err.message : 'Failed to load token');
        } finally {
            setLoading(false);
        }
    }, [token]);

    /**
     * Submit single-step form
     * @param {Object} data - Form data to submit
     * @returns {Promise<import('../types/publicToken.types').TokenSubmitResponse>}
     */
    const submitForm = useCallback(async (data) => {
        if (!token) {
            throw new Error('No token provided');
        }

        setSubmitting(true);
        try {
            const result = await publicTokenService.submitToken(token, data);
            return result;
        } catch (err) {
            console.error('Failed to submit form:', err);
            throw err;
        } finally {
            setSubmitting(false);
        }
    }, [token]);

    /**
     * Update form data
     * @param {string} fieldName - Field name
     * @param {*} value - Field value
     */
    const updateField = useCallback((fieldName, value) => {
        setFormData(prev => ({
            ...prev,
            [fieldName]: value
        }));
    }, []);

    /**
     * Update multiple fields at once
     * @param {Object} fields - Object with field name/value pairs
     */
    const updateFields = useCallback((fields) => {
        setFormData(prev => ({
            ...prev,
            ...fields
        }));
    }, []);

    /**
     * Reset form data to initial state (payload or empty)
     */
    const resetForm = useCallback(() => {
        setFormData(tokenData?.payload || {});
    }, [tokenData]);

    /**
     * Check if this is a multi-step form
     */
    const isMultiStep = tokenData ? publicTokenService.isMultiStepForm(tokenData) : false;

    /**
     * Get time remaining until expiration
     */
    const timeRemaining = tokenData?.expiresAt 
        ? publicTokenService.getTimeRemaining(tokenData.expiresAt)
        : null;

    // Load token on mount or when token changes
    useEffect(() => {
        loadToken();
    }, [loadToken]);

    return {
        // State
        tokenData,
        loading,
        error,
        submitting,
        formData,
        
        // Computed
        isMultiStep,
        timeRemaining,
        isValid: tokenData?.valid === true,
        isExpired: timeRemaining?.expired === true,
        
        // Actions
        loadToken,
        submitForm,
        updateField,
        updateFields,
        resetForm,
    };
};

/**
 * Hook for managing multi-step token forms with progress saving
 * @param {string} token - Token ID from URL
 * @returns {Object} Multi-step form state and management functions
 */
export const useMultiStepTokenForm = (token) => {
    const [tokenData, setTokenData] = useState(null);
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(false);
    const [completing, setCompleting] = useState(false);
    const [currentStepData, setCurrentStepData] = useState({});

    /**
     * Load token and progress data
     */
    const loadForm = useCallback(async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setError(null);

            // Load token validation
            const tokenResponse = await publicTokenService.validateToken(token);
            setTokenData(tokenResponse);

            if (!tokenResponse.valid) {
                setError(tokenResponse.message || 'Invalid token');
                return;
            }

            // If multi-step, load progress
            if (publicTokenService.isMultiStepForm(tokenResponse)) {
                const progressData = await publicTokenService.getProgress(token);
                setProgress(progressData);

                // Load current step data
                const stepNum = progressData.currentStep.toString();
                if (progressData.stepData && progressData.stepData[stepNum]) {
                    setCurrentStepData(progressData.stepData[stepNum]);
                }
            }
        } catch (err) {
            console.error('Failed to load form:', err);
            setError(err instanceof Error ? err.message : 'Failed to load form');
        } finally {
            setLoading(false);
        }
    }, [token]);

    /**
     * Save progress for current step
     * @param {Object} stepData - Data to save for current step
     * @param {Object} options - Save options
     * @param {boolean} [options.markComplete=false] - Mark step as complete
     * @param {boolean} [options.moveNext=false] - Move to next step
     * @returns {Promise<import('../types/publicToken.types').SaveProgressResponse>}
     */
    const saveStep = useCallback(async (stepData, options = {}) => {
        if (!token) {
            throw new Error('No token provided');
        }

        setSaving(true);
        try {
            const result = await publicTokenService.saveProgress(token, {
                stepNumber: progress?.currentStep,
                stepData,
                markStepComplete: options.markComplete || false,
                moveToNextStep: options.moveNext || false,
            });

            if (result.success) {
                // Reload progress to get updated state
                await loadForm();
            }

            return result;
        } catch (err) {
            console.error('Failed to save progress:', err);
            throw err;
        } finally {
            setSaving(false);
        }
    }, [token, progress, loadForm]);

    /**
     * Complete the multi-step form
     * @param {Object} finalStepData - Data for the final step
     * @param {Object} [allStepData] - Optional override for all steps
     * @returns {Promise<import('../types/publicToken.types').CompleteFormResponse>}
     */
    const completeForm = useCallback(async (finalStepData, allStepData = null) => {
        if (!token) {
            throw new Error('No token provided');
        }

        setCompleting(true);
        try {
            const result = await publicTokenService.completeForm(token, {
                finalStepData,
                allStepData,
            });

            return result;
        } catch (err) {
            console.error('Failed to complete form:', err);
            throw err;
        } finally {
            setCompleting(false);
        }
    }, [token]);

    /**
     * Update current step data
     * @param {string} fieldName - Field name
     * @param {*} value - Field value
     */
    const updateField = useCallback((fieldName, value) => {
        setCurrentStepData(prev => ({
            ...prev,
            [fieldName]: value
        }));
    }, []);

    /**
     * Update multiple fields in current step
     * @param {Object} fields - Object with field name/value pairs
     */
    const updateFields = useCallback((fields) => {
        setCurrentStepData(prev => ({
            ...prev,
            ...fields
        }));
    }, []);

    /**
     * Navigate to a specific step
     * @param {number} stepNumber - Step number (1-based)
     */
    const goToStep = useCallback(async (stepNumber) => {
        if (!progress) return;

        // Save current step data first
        if (Object.keys(currentStepData).length > 0) {
            await saveStep(currentStepData, { markComplete: false, moveNext: false });
        }

        // Update local state
        const stepNum = stepNumber.toString();
        if (progress.stepData && progress.stepData[stepNum]) {
            setCurrentStepData(progress.stepData[stepNum]);
        } else {
            setCurrentStepData({});
        }
    }, [progress, currentStepData, saveStep]);

    /**
     * Get fields for a specific step
     * @param {number} stepNumber - Step number
     * @returns {import('../types/secureForm.types').FormField[]}
     */
    const getStepFields = useCallback((stepNumber) => {
        if (!tokenData?.formSchema?.schema?.fields) return [];
        return tokenData.formSchema.schema.fields.filter(field => field.step === stepNumber);
    }, [tokenData]);

    /**
     * Get section for a specific step
     * @param {number} stepNumber - Step number
     * @returns {import('../types/secureForm.types').FormSection | null}
     */
    const getStepSection = useCallback((stepNumber) => {
        if (!tokenData?.formSchema?.schema?.sections) return null;
        return tokenData.formSchema.schema.sections.find(
            section => section.id === `step${stepNumber}`
        ) || null;
    }, [tokenData]);

    // Load form on mount or when token changes
    useEffect(() => {
        loadForm();
    }, [loadForm]);

    return {
        // State
        tokenData,
        progress,
        loading,
        error,
        saving,
        completing,
        currentStepData,
        
        // Computed
        currentStep: progress?.currentStep || 1,
        totalSteps: progress?.totalSteps || 0,
        completedSteps: progress?.completedSteps || [],
        isCompleted: progress?.isCompleted || false,
        isValid: tokenData?.valid === true,
        
        // Actions
        loadForm,
        saveStep,
        completeForm,
        updateField,
        updateFields,
        goToStep,
        getStepFields,
        getStepSection,
    };
};

export default useTokenForm;
